import type { ItEmailAction, ItEmailPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { STR } from '../../../../i18n';
import {
  ApiError,
  completeItEmailItem,
  confirmAllItEmail,
  listItEmailMemberships,
  listItEmails,
  takeBackItEmail,
} from '../../../../lib/api';
import { queryKeys } from '../../../../lib/queryKeys';
import { showToast } from '../../../../lib/toast';

import { type ItStanding, itStanding } from './picker';

/**
 * THE IT STEP'S READS (spec row 15). Two reads, both separate from the Queue so no existing
 * Queue response changes: every IT email (the lane and the page), and which records sit in a
 * live one (the lanes and the picker). Both answer the same for an administrator and for HR.
 */
export function useItEmails() {
  return useQuery({ queryKey: queryKeys.itEmails, queryFn: () => listItEmails() });
}

/**
 * Until `ready`, `standing` is empty: a count drawn from it would leave every record in its own
 * lane, so a view that counts lanes waits for it (and offers `refetch` when the read failed).
 */
export function useItStanding(): {
  standing: ItStanding;
  ready: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const memberships = useQuery({
    queryKey: queryKeys.itEmailMemberships,
    queryFn: listItEmailMemberships,
  });
  const standing = useMemo(() => itStanding(memberships.data?.data ?? []), [memberships.data]);
  return {
    standing,
    ready: memberships.data !== undefined,
    isError: memberships.isError,
    refetch: () => {
      void memberships.refetch();
    },
  };
}

export type ItEmailOp =
  | { kind: 'item'; email: ItEmailPublic; itemId: string; action: ItEmailAction }
  | { kind: 'confirmAll'; email: ItEmailPublic }
  | { kind: 'takeBack'; email: ItEmailPublic };

function run(op: ItEmailOp) {
  switch (op.kind) {
    case 'item':
      return completeItEmailItem(op.email.id, op.itemId, op.action);
    case 'confirmAll':
      return confirmAllItEmail(op.email.id);
    case 'takeBack':
      return takeBackItEmail(op.email.id);
  }
}

/**
 * Mark one item granted / removed, IT confirmed all, Take back. Each is one server
 * transaction that also moves the access record, so every access view re-reads afterwards.
 */
export function useItEmailActions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (_result, op) => {
      if (op.kind === 'item') {
        showToast(op.action === 'set_up' ? STR.itEmails.itemGranted : STR.itEmails.itemRemoved);
      } else if (op.kind === 'confirmAll') {
        showToast(STR.itEmails.confirmedAll(op.email.openItemCount));
      } else {
        showToast(op.email.status === 'draft' ? STR.itEmails.discarded : STR.itEmails.takenBack);
      }
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
    },
  });
}
